import { z } from 'zod'

// Email status types
export type EmailStatus = 'pending' | 'queued' | 'sending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed'

// Email template types
export type EmailTemplateType = 'relance_devis' | 'relance_facture' | 'bienvenue' | 'notification' | 'custom'

// Email priority for queue
export type EmailPriority = 'low' | 'normal' | 'high' | 'urgent'

// Email template interface
export interface EmailTemplate {
  id: string
  created_at: string
  updated_at: string
  name: string
  type: EmailTemplateType
  subject: string
  html_content: string
  text_content: string | null
  variables: string[] // Available variables like {{client_name}}, {{montant}}
  is_active: boolean
  created_by: string | null
}

// Email log interface
export interface EmailLog {
  id: string
  created_at: string
  updated_at: string
  template_id: string | null
  template?: EmailTemplate
  recipient_email: string
  recipient_name: string | null
  subject: string
  html_content: string
  text_content: string | null
  status: EmailStatus
  sent_at: string | null
  delivered_at: string | null
  opened_at: string | null
  clicked_at: string | null
  bounced_at: string | null
  failed_at: string | null
  error_message: string | null
  retry_count: number
  max_retries: number
  reference_type: string | null // 'devis', 'facture', 'client', etc.
  reference_id: string | null
  metadata: Record<string, unknown> | null
  sent_by: string | null
}

// Email queue item
export interface EmailQueueItem {
  id: string
  created_at: string
  scheduled_at: string
  priority: EmailPriority
  template_id: string | null
  recipient_email: string
  recipient_name: string | null
  subject: string
  html_content: string
  text_content: string | null
  variables: Record<string, string>
  reference_type: string | null
  reference_id: string | null
  metadata: Record<string, unknown> | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  retry_count: number
  max_retries: number
  error_message: string | null
}

// Email settings interface
export interface EmailSettings {
  id: string
  created_at: string
  updated_at: string
  // SMTP Configuration
  smtp_host: string
  smtp_port: number
  smtp_secure: boolean
  smtp_user: string
  smtp_password: string // Encrypted in database
  // Resend Configuration (alternative)
  resend_api_key: string | null // Encrypted in database
  use_resend: boolean
  // Sender Configuration
  from_email: string
  from_name: string
  reply_to_email: string | null
  // Sending Schedule
  sending_enabled: boolean
  allowed_sending_start: string // Time in HH:MM format
  allowed_sending_end: string // Time in HH:MM format
  allowed_sending_days: number[] // 0=Sunday, 1=Monday, etc.
  timezone: string
  // Rate Limiting
  max_emails_per_hour: number
  max_emails_per_day: number
  // Retry Configuration
  retry_enabled: boolean
  max_retry_count: number
  retry_delay_minutes: number
}

// Email send request
export interface SendEmailRequest {
  to: string
  toName?: string
  subject: string
  html: string
  text?: string
  templateId?: string
  variables?: Record<string, string>
  priority?: EmailPriority
  scheduledAt?: string
  referenceType?: string
  referenceId?: string
  metadata?: Record<string, unknown>
  attachments?: { filename: string; content: string; content_type?: string }[]
}

// Email send response
export interface SendEmailResponse {
  success: boolean
  messageId?: string
  queueId?: string
  error?: string
}

// Email stats
export interface EmailStats {
  total_sent: number
  total_delivered: number
  total_opened: number
  total_clicked: number
  total_bounced: number
  total_failed: number
  delivery_rate: number
  open_rate: number
  click_rate: number
  bounce_rate: number
}

// Validation schemas
export const emailRecipientSchema = z.string().email('Adresse email invalide').min(5, 'Email trop court').max(254, 'Email trop long')

export const sendEmailSchema = z.object({
  to: emailRecipientSchema,
  toName: z.string().max(100).optional(),
  subject: z.string().min(1, 'Le sujet est requis').max(200, 'Sujet trop long'),
  html: z.string().min(1, 'Le contenu HTML est requis'),
  text: z.string().optional(),
  templateId: z.string().uuid().optional(),
  variables: z.record(z.string()).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  scheduledAt: z.string().datetime().optional(),
  referenceType: z.string().max(50).optional(),
  referenceId: z.string().uuid().optional(),
  metadata: z.record(z.unknown()).optional(),
  attachments: z.array(z.object({
    filename: z.string().min(1).max(200),
    content: z.string().min(1),
    content_type: z.string().optional(),
  })).max(5).optional(),
})

export const emailTemplateSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caracteres').max(100, 'Nom trop long'),
  type: z.enum(['relance_devis', 'relance_facture', 'bienvenue', 'notification', 'custom']),
  subject: z.string().min(1, 'Le sujet est requis').max(200, 'Sujet trop long'),
  html_content: z.string().min(1, 'Le contenu HTML est requis'),
  text_content: z.string().nullable().optional(),
  variables: z.array(z.string()).optional(),
  is_active: z.boolean().optional(),
})

export const emailSettingsSchema = z.object({
  smtp_host: z.string().min(1, 'Host SMTP requis'),
  smtp_port: z.number().min(1).max(65535),
  smtp_secure: z.boolean(),
  smtp_user: z.string().min(1, 'Utilisateur SMTP requis'),
  smtp_password: z.string().min(1, 'Mot de passe SMTP requis'),
  resend_api_key: z.string().nullable().optional(),
  use_resend: z.boolean(),
  from_email: emailRecipientSchema,
  from_name: z.string().min(1, 'Nom expediteur requis').max(100),
  reply_to_email: z.string().email().nullable().optional(),
  sending_enabled: z.boolean(),
  allowed_sending_start: z.string().regex(/^\d{2}:\d{2}$/, 'Format HH:MM requis'),
  allowed_sending_end: z.string().regex(/^\d{2}:\d{2}$/, 'Format HH:MM requis'),
  allowed_sending_days: z.array(z.number().min(0).max(6)),
  timezone: z.string().min(1, 'Timezone requis'),
  max_emails_per_hour: z.number().min(1).max(1000),
  max_emails_per_day: z.number().min(1).max(10000),
  retry_enabled: z.boolean(),
  max_retry_count: z.number().min(0).max(10),
  retry_delay_minutes: z.number().min(1).max(1440),
})

export type EmailTemplateFormData = z.infer<typeof emailTemplateSchema>
export type EmailSettingsFormData = z.infer<typeof emailSettingsSchema>
export type SendEmailFormData = z.infer<typeof sendEmailSchema>

// Filters for email logs
export interface EmailLogFilters {
  status?: EmailStatus
  templateId?: string
  referenceType?: string
  referenceId?: string
  startDate?: string
  endDate?: string
  search?: string
  page?: number
  limit?: number
}

// Default template variables
export const DEFAULT_TEMPLATE_VARIABLES: Record<EmailTemplateType, string[]> = {
  relance_devis: [
    '{{client_name}}',
    '{{client_email}}',
    '{{devis_numero}}',
    '{{montant_ht}}',
    '{{montant_ttc}}',
    '{{date_validite}}',
    '{{lien_devis}}',
    '{{entreprise_nom}}',
  ],
  relance_facture: [
    '{{client_name}}',
    '{{client_email}}',
    '{{facture_numero}}',
    '{{montant_ht}}',
    '{{montant_ttc}}',
    '{{date_echeance}}',
    '{{jours_retard}}',
    '{{lien_facture}}',
    '{{entreprise_nom}}',
  ],
  bienvenue: [
    '{{client_name}}',
    '{{client_email}}',
    '{{lien_espace_client}}',
    '{{entreprise_nom}}',
    '{{contact_email}}',
    '{{contact_telephone}}',
  ],
  notification: [
    '{{client_name}}',
    '{{notification_titre}}',
    '{{notification_message}}',
    '{{lien_action}}',
    '{{entreprise_nom}}',
  ],
  custom: [],
}
